import { dateKeyInTimeZone, formatCalendarDate } from '@/lib/date-values';
import type { Invoice } from '@/types/invoice';
import { getInvoiceFinancialSummary } from './financial-summary';

/**
 * What an invoice SHOWS while bank money clears. The server's stored
 * `amountPaid` / `balanceDue` move only when the bank settles; every screen
 * counts clearing bank money as paid, with a note, through this one helper.
 *
 * Settlement books the cash on `amountPaid` and any earned pay-by-bank
 * discount as a credit memo on `creditsApplied`, so Paid adds the cash,
 * Credits the discount, and Balance loses the whole coverage. Each figure
 * then equals what the invoice stores the moment the payment settles. The
 * server keeps only the coverage predicates
 * (`apps/operations/.../utils/shown-invoice-money.ts`); keep their cent rules
 * in step with `covers` here.
 *
 * A viewer the server withholds the clearing figures from (the masks that
 * hide the balance) gets null clearing fields, and this returns the stored
 * figures with no note.
 */
export type ShownMoneyInput = Pick<Invoice, 'total' | 'amountPaid' | 'balanceDue'> &
  Partial<
    Pick<
      Invoice,
      | 'financialFieldPresence'
      | 'creditsApplied'
      | 'clearingBankTotal'
      | 'clearingBankCash'
      | 'provisionalClearingTotal'
      | 'clearingSince'
    >
  >;

export interface ShownInvoiceMoney {
  /** Null when the server withheld the figure from this viewer: render the masked placeholder. */
  paid: number | null;
  credits: number;
  /** Null when the balance itself is masked or cannot be derived. */
  balance: number | null;
  /** The amount "$X still clearing" names; 0 when nothing is clearing. */
  clearingNote: number;
  /** The bank money clearing covers the whole balance. */
  covers: boolean;
}

export function cents(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.round(value * 100) : 0;
}

/**
 * Whether the invoice has money in flight the screen is waiting on: a debit
 * made on the invoice (`settlingPayment`), or clearing bank money the figures
 * already count, including a contact-page ACH applied as credit, which has no
 * `settlingPayment`. The invoice hooks poll while this holds, so a Clearing
 * invoice updates on its own when the bank settles or returns the money.
 */
export function hasClearingMoneyInFlight(
  invoice: Pick<Invoice, 'settlingPayment' | 'clearingBankTotal' | 'provisionalClearingTotal'>,
): boolean {
  return (
    Boolean(invoice.settlingPayment) ||
    cents(invoice.clearingBankTotal) > 0 ||
    cents(invoice.provisionalClearingTotal) > 0
  );
}

export function shownInvoiceMoney(invoice: ShownMoneyInput): ShownInvoiceMoney {
  // Reuse the masked-balance rules: a missing balance stays unknown.
  const summary = getInvoiceFinancialSummary({
    subtotal: 0,
    discountTotal: 0,
    membershipDiscountTotal: 0,
    taxTotal: 0,
    total: invoice.total,
    amountPaid: invoice.amountPaid,
    balanceDue: invoice.balanceDue,
    financialFieldPresence: invoice.financialFieldPresence,
  });
  const coverage = Math.max(0, cents(invoice.clearingBankTotal));
  const cash =
    invoice.clearingBankCash === null || invoice.clearingBankCash === undefined
      ? coverage
      : Math.min(coverage, Math.max(0, cents(invoice.clearingBankCash)));
  const provisional = Math.max(0, cents(invoice.provisionalClearingTotal));
  const balanceCents =
    summary.balanceDue === undefined ? undefined : Math.max(0, cents(summary.balanceDue));
  const paidWithheld = invoice.financialFieldPresence?.amountPaid === false;
  return {
    paid: paidWithheld ? null : (cents(summary.amountPaid) + cash) / 100,
    credits: (cents(invoice.creditsApplied) + coverage - cash) / 100,
    balance: balanceCents === undefined ? null : Math.max(0, balanceCents - coverage) / 100,
    clearingNote: (cash + provisional) / 100,
    covers: balanceCents !== undefined && balanceCents > 0 && coverage >= balanceCents,
  };
}

/**
 * "Sep 28": the calendar day the money was submitted in the COMPANY's
 * timezone, never the viewer's (the tenant-day rule).
 */
function shortDay(iso: string | null | undefined, timeZone: string | null | undefined) {
  if (!iso || Number.isNaN(new Date(iso).getTime())) return null;
  const day = formatCalendarDate(dateKeyInTimeZone(iso, timeZone), {
    month: 'short',
    day: 'numeric',
  });
  return day || null;
}

/**
 * Staff, under Paid: "$250.00 still clearing · ACH Sep 28". Pass the company
 * timezone (`useCompanyTimezone`); the day is not the viewer's.
 */
export function staffClearingNote(
  invoice: ShownMoneyInput,
  formatMoney: (amount: number) => string,
  timeZone: string | null | undefined,
): string | null {
  const { clearingNote } = shownInvoiceMoney(invoice);
  if (!(clearingNote > 0)) return null;
  const day = shortDay(invoice.clearingSince, timeZone);
  return `${formatMoney(clearingNote)} still clearing${day ? ` · ACH ${day}` : ''}`;
}

/** Staff rows: "$250.00 paid, clearing". */
export function clearingPaidText(
  invoice: ShownMoneyInput,
  formatMoney: (amount: number) => string,
): string | null {
  const { clearingNote } = shownInvoiceMoney(invoice);
  return clearingNote > 0 ? `${formatMoney(clearingNote)} paid, clearing` : null;
}

/**
 * Customer documents, under the totals ("*" added by the renderer):
 * "Includes a $250.00 bank payment made Sep 28 that is still processing."
 * Customers see "processing", never "clearing". The day is the COMPANY's
 * calendar day (pass its timezone), never the viewer's.
 */
export function customerClearingFootnote(
  invoice: ShownMoneyInput,
  formatMoney: (amount: number) => string,
  timeZone: string,
): string | null {
  const { clearingNote } = shownInvoiceMoney(invoice);
  if (!(clearingNote > 0)) return null;
  const day = shortDay(invoice.clearingSince, timeZone);
  return `Includes a ${formatMoney(clearingNote)} bank payment${
    day ? ` made ${day}` : ''
  } that is still processing.`;
}
